import React from 'react';
import { useNotification } from '@/common/components/notification/NotificationContext';
import {
  ProjectVersionType,
  useGetProjectVersionQuery,
  ProjectFileType,
  useFileDownloadMutation,
} from '@/generated/graphql';
import { blobToBase64 } from '@/common/utils/binaryUtils';
import { useTransferManagerContext } from '@/context/TransferManagerContext';
import { useDirectoryPicker } from '@/hooks/useDirectoryPicker';
import { useProjectDirectories } from '@/context/ProjectDirectoriesContext';
import { useIsElectron, useElectronAPI } from '@/context/ElectronContext';
import { desktopBridge } from '@/services/desktopBridge';
import { useBridgeRequired } from '@/context/BridgeRequiredContext';
import { FileSyncStatus } from '@/common/models/enums';
import {
  assertSafeRelativePath,
  verifyDownloadedFile,
} from '@/common/utils/fileIntegrity';
import { assertUniqueFilePaths } from '@/common/utils/projectManifestUtils';
import {
  createTransferDiagnostics,
  reportTransferError,
} from '@/services/transferDiagnostics';

export interface SyncDialogData {
  newerFiles: number;
  behindFiles: number;
  deletedFiles: number;
  newFiles: number;
  fileStatuses: FileStatus[];
  syncProjectDir: string | FileSystemDirectoryHandle;
  versionFiles: ProjectFileType[];
  version: ProjectVersionType;
  onConfirmSync: () => Promise<void>;
}

interface UseProjectVersionSyncProps {
  project: { _id: string; name?: string };
  version: ProjectVersionType;
  isExpandedVersion: boolean;
  projectDir?: string | FileSystemDirectoryHandle | null;
  showSyncDialog: (data: SyncDialogData) => void;
}

interface FileStatus {
  fileName: string;
  status: FileSyncStatus;
  isLocal: boolean;
}

export const useProjectVersionSync = ({
  project,
  version,
  isExpandedVersion,
  projectDir,
  showSyncDialog,
}: UseProjectVersionSyncProps) => {
  const { showError, showSuccess } = useNotification();
  const { enqueueDownload } = useTransferManagerContext();
  const [fileDownload] = useFileDownloadMutation();
  const { selectDirectory } = useDirectoryPicker({ validateNonEmpty: false });
  const { setProjectDirectory, getStoredProjectPath } = useProjectDirectories();
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();
  const { requireBridge } = useBridgeRequired();
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);

  // Helper to get project path (string) from projectDir
  const getProjectPath = (): string | null => {
    if (typeof projectDir === 'string') {
      return projectDir;
    }
    if (isElectron && project?._id) {
      return getStoredProjectPath(project._id);
    }
    return null;
  };

  // No longer need dialog state - handled by parent

  // Check if we need to load the previous version (when current version has deleted files)
  const shouldLoadPreviousVersion =
    version.deletedFiles && version.deletedFiles.length > 0;
  const previousVersionNumber =
    version.version > 1 ? version.version - 1 : null;

  // GraphQL query for current version data
  const {
    data: latestVersionData,
    loading: latestVersionLoading,
    error: latestVersionError,
    refetch,
  } = useGetProjectVersionQuery({
    variables: {
      projectId: project._id,
      version: version.version,
    },
    skip: !project || !isExpandedVersion,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // GraphQL query for previous version data (only when needed for deleted files)
  const {
    data: previousVersionData,
    loading: previousVersionLoading,
    error: previousVersionError,
  } = useGetProjectVersionQuery({
    variables: {
      projectId: project._id,
      version: previousVersionNumber || 1,
    },
    skip:
      !project ||
      !shouldLoadPreviousVersion ||
      !previousVersionNumber ||
      !isExpandedVersion,
    fetchPolicy: 'cache-first', // Now safe to use cache with proper type policies
    nextFetchPolicy: 'cache-first',
  });

  // Show error notifications
  if (latestVersionError) {
    showError('Error loading version :(');
  }
  if (previousVersionError) {
    showError('Error loading previous version data :(');
  }
  React.useEffect(() => {
    if (latestVersionError)
      reportTransferError('Load selected version', latestVersionError, {
        projectId: project._id,
        version: version.version,
      });
    if (previousVersionError)
      reportTransferError('Load previous version', previousVersionError, {
        projectId: project._id,
        version: previousVersionNumber ?? undefined,
      });
  }, [
    latestVersionError,
    previousVersionError,
    project._id,
    version.version,
    previousVersionNumber,
  ]);

  // Custom checkStatus function for version comparison

  // Helper function to delete a local file (Electron/bridge only for sync)
  const deleteLocalFile = async (
    filePath: string,
    customDir?: string | FileSystemDirectoryHandle
  ): Promise<void> => {
    const projectPath =
      typeof customDir === 'string' ? customDir : getProjectPath();

    if (!projectPath) {
      throw new Error('No project directory');
    }

    try {
      if (isElectron && electronAPI && electronAPI.deleteFile) {
        const fullPath = `${projectPath}/${filePath}`;
        const result = await electronAPI.deleteFile(fullPath);
        if (!result.success) {
          throw new Error(result.error || 'Failed to delete file');
        }
      } else {
        // Web: Use desktop bridge
        if (!(await requireBridge('fileDelete'))) {
          throw new Error('Desktop app required for file operations');
        }
        const fullPath = `${projectPath}/${filePath}`;
        const result = await desktopBridge.deleteFile(fullPath);
        if (!result.success) {
          throw new Error(result.error || 'Failed to delete file');
        }
      }
    } catch (error) {
      console.error(`Error deleting local file: ${filePath}`, error);
      throw error;
    }
  };

  // Helper function to download a file
  const downloadFile = async (
    file: ProjectFileType,
    customDir?: string | FileSystemDirectoryHandle
  ): Promise<{ success: boolean; error?: string; cause?: unknown }> => {
    try {
      const { fileName, _id: fileId } = file;
      const result = await fileDownload({
        variables: {
          data: {
            projectId: project._id,
            fileId,
          },
        },
      });

      if (!result.data?.fileDownload?.presignedUrl) {
        return { success: false, error: 'Failed to get download URL' };
      }

      const response = await fetch(result.data.fileDownload.presignedUrl);
      if (!response.ok) {
        return { success: false, error: 'Failed to download file' };
      }

      const blob = await response.blob();
      await verifyDownloadedFile(blob, file);

      // Native/bridge path: both environments use a string project path and
      // delegate directory creation plus binary writes to the desktop app.
      const projectPath =
        typeof customDir === 'string' ? customDir : getProjectPath();
      if (projectPath) {
        const base64Content = await blobToBase64(blob);
        const fullPath = `${projectPath}/${fileName}`;
        let writeResult: { success: boolean; error?: string };

        if (isElectron && electronAPI) {
          writeResult = await electronAPI.writeFile(
            fullPath,
            base64Content,
            'base64'
          );
        } else {
          if (!(await requireBridge('fileWrite'))) {
            return {
              success: false,
              error: 'Desktop app required for file operations',
            };
          }
          writeResult = await desktopBridge.writeFile(
            fullPath,
            base64Content,
            'base64'
          );
        }

        return writeResult.success
          ? { success: true }
          : {
              success: false,
              error: writeResult.error || 'Failed to write file',
            };
      }

      return {
        success: false,
        error: 'No project directory path available',
      };
    } catch (error) {
      console.error(`Error downloading file ${file.fileName}:`, error);
      return {
        cause: error,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  // Function to perform the actual sync
  const performSync = async (
    fileStatuses: FileStatus[],
    syncProjectDir: string | FileSystemDirectoryHandle,
    versionFiles: ProjectFileType[]
  ) => {
    const diagnostics = createTransferDiagnostics('Sync to version', {
      projectId: project._id,
      projectName: project.name,
      version: version.version,
      files: versionFiles.map((file) => ({
        ...file,
        status: fileStatuses.find((status) => status.fileName === file.fileName)
          ?.status,
      })),
    });
    try {
      assertUniqueFilePaths(versionFiles);
      versionFiles.forEach((file) => assertSafeRelativePath(file.fileName));
      fileStatuses.forEach((file) => assertSafeRelativePath(file.fileName));
      if (fileStatuses.some((file) => file.status === FileSyncStatus.UNKNOWN))
        throw new Error(
          'Some local files could not be read. Retry after saving finishes.'
        );
    } catch (error) {
      diagnostics.add('Validate restore', error);
      diagnostics.publish();
      showError(
        error instanceof Error
          ? error.message
          : 'Cannot safely sync these files'
      );
      return;
    }
    const filesToSync = fileStatuses.filter(
      (f) => f.status !== FileSyncStatus.UP_TO_DATE
    );
    showSuccess(`Syncing ${filesToSync.length} file changes...`);

    let completed = 0;
    let hasErrors = false;

    // Handle deletions (files that exist locally but not in this version)
    // Only delete files marked as "new" (local-only files)
    const filesToDelete = fileStatuses.filter(
      (f) => f.status === FileSyncStatus.NEW
    );

    // Handle downloads (files that need to be added or updated)
    // This includes "behind" (outdated), "deleted" (exists in version but not locally), and "newer" (though we'll overwrite with version content)
    const filesToDownload = fileStatuses.filter(
      (f) =>
        f.status === FileSyncStatus.BEHIND ||
        f.status === FileSyncStatus.DELETED ||
        f.status === FileSyncStatus.NEWER
    );

    for (const fileStatus of filesToDownload) {
      const versionFile = versionFiles.find(
        (vf) => vf.fileName === fileStatus.fileName
      );
      if (versionFile) {
        const result = await downloadFile(versionFile, syncProjectDir);
        if (result.success) {
          completed++;
          showSuccess(
            `Updated ${versionFile.fileName} (${completed}/${filesToSync.length})`
          );
        } else {
          hasErrors = true;
          diagnostics.add(
            'Restore file',
            result.cause ?? result.error,
            versionFile
          );
          showError(`Failed to sync ${versionFile.fileName}: ${result.error}`);
        }
      } else {
        hasErrors = true;
        diagnostics.add(
          'Resolve selected version file',
          'Missing version metadata',
          fileStatus
        );
        showError(`Missing version metadata for ${fileStatus.fileName}`);
      }
    }

    // Destructive restore is intentional, but never delete unuploaded work
    // when a prerequisite download or replacement has failed.
    if (!hasErrors)
      for (const fileStatus of filesToDelete) {
        try {
          await deleteLocalFile(fileStatus.fileName, syncProjectDir);
          completed++;
          showSuccess(
            `Deleted ${fileStatus.fileName} (${completed}/${filesToSync.length})`
          );
        } catch (error) {
          hasErrors = true;
          diagnostics.add('Delete local-only file', error, fileStatus);
          showError(`Failed to delete ${fileStatus.fileName}`);
        }
      }

    if (hasErrors) diagnostics.publish();
    if (!hasErrors) {
      showSuccess(`Successfully synced to version ${version.version}!`);
    } else if (completed > 0) {
      showSuccess(
        `Partially synced to version ${version.version} (${completed}/${filesToSync.length} files updated)`
      );
    }
  };

  // No longer need dialog handlers - handled by parent

  const handleDownloadVersion = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion toggle when clicking download button

    try {
      let versionFiles = latestVersionData?.projectVersion?.files;

      // If files haven't been fetched yet, fetch them now
      if (!versionFiles && project) {
        showSuccess('Fetching version files...');

        const { data } = await refetch({
          projectId: project._id,
          version: version.version,
        });

        versionFiles = data?.projectVersion?.files;
      }

      if (!versionFiles || versionFiles.length === 0) {
        showError('No files available to download for this version');
        return;
      }

      // Let user select a download directory

      let downloadTarget: string | FileSystemDirectoryHandle | null = null;

      // Try Electron API first if available
      if (window.electronAPI?.selectFolder) {
        try {
          const folderPath = await window.electronAPI.selectFolder();
          if (folderPath) {
            downloadTarget = folderPath;
          }
        } catch (error) {
          // Electron folder picker failed, fallback to browser
        }
      }

      // Fallback to browser picker if Electron failed or not available
      if (!downloadTarget) {
        downloadTarget = await selectDirectory();
      }

      if (!downloadTarget) {
        // User cancelled or there was an error
        return;
      }

      const downloadRequests = versionFiles.map((file: ProjectFileType) => ({
        fileId: file._id,
        fileName: file.fileName,
        size: file.size,
        fullHash: file.fullHash,
      }));

      const writeTarget = await enqueueDownload({
        version: version.version,
        projectId: project._id,
        projectName: project.name || 'Project',
        files: downloadRequests,
        dirHandle: downloadTarget,
        projectFolderName: project.name || 'Project',
      });

      // Persist the actual project folder used by download (child folder),
      // so subsequent Sync operations target the correct directory.
      if (writeTarget) {
        if (typeof writeTarget === 'string') {
          const mockHandle = {
            name: writeTarget.split(/[/\\]/).pop() || project.name || 'Project',
            kind: 'directory' as const,
          } as FileSystemDirectoryHandle;
          await setProjectDirectory(project._id, mockHandle, writeTarget);
        } else {
          await setProjectDirectory(project._id, writeTarget);
        }

        showSuccess(`Started downloading version ${version.version} files`);
      }
    } catch (error) {
      console.error('Error downloading version:', error);
      reportTransferError('Download version', error, {
        projectId: project._id,
        projectName: project.name,
        version: version.version,
      });
      showError('Failed to download version files. Please try again.');
    }
  };

  const handleSyncToVersion = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion toggle

    if (!isElectron && !(await requireBridge('versionSync'))) return;

    setIsAnalyzing(true);

    let syncProjectDir: string | FileSystemDirectoryHandle | null = null;
    // Get project path if available
    if (project._id) {
      const projectPath = getProjectPath();
      if (projectPath) {
        syncProjectDir = projectPath;
      }
    }

    // If no project directory is selected, prompt user to select one
    if (!syncProjectDir) {
      if (isElectron && electronAPI) {
        const folderPath = await electronAPI.selectFolder();
        if (folderPath) {
          syncProjectDir = folderPath;
          // Create mock handle for storage
          const mockHandle = {
            name: folderPath.split(/[/\\]/).pop() || 'Project',
            kind: 'directory' as const,
          } as FileSystemDirectoryHandle;
          await setProjectDirectory(project._id, mockHandle, folderPath);
        }
      } else {
        const folderPath = await desktopBridge.selectFolder();
        if (folderPath) {
          syncProjectDir = folderPath;
          const mockHandle = {
            name: folderPath.split(/[/\\]/).pop() || 'Project',
            kind: 'directory' as const,
          } as FileSystemDirectoryHandle;
          await setProjectDirectory(project._id, mockHandle, folderPath);
        }
      }

      if (!syncProjectDir) {
        // User cancelled or there was an error
        setIsAnalyzing(false);
        return;
      }
    }

    try {
      let versionFiles = latestVersionData?.projectVersion?.files;

      // Always fetch version files if not available (regardless of expansion state)
      if (!versionFiles && project) {
        showSuccess('Fetching version files...');

        const { data } = await refetch({
          projectId: project._id,
          version: version.version,
        });

        versionFiles = data?.projectVersion?.files;
      }

      if (!versionFiles) {
        showError('No files available for this version');
        return;
      }

      showSuccess('Analyzing local files...');

      // Create a custom checkStatus function that uses the fetched versionFiles
      const checkStatusWithVersionFiles = async (
        customDir?: string | FileSystemDirectoryHandle
      ): Promise<FileStatus[]> => {
        const projectPath =
          typeof customDir === 'string' ? customDir : getProjectPath();

        if (!projectPath && !customDir) {
          return [];
        }

        try {
          // Use Electron sync manager or desktop bridge
          if (isElectron && electronAPI && projectPath) {
            const result = await electronAPI.requestSyncCheck(
              project._id,
              projectPath,
              versionFiles
            );
            if (result.success && result.fileStatuses) {
              return result.fileStatuses.map((fs) => ({
                ...fs,
                status: fs.status as FileSyncStatus,
              }));
            }
          } else if (projectPath) {
            // Web: Use desktop bridge
            if (!(await requireBridge('versionSync'))) {
              return [];
            }
            const result = await desktopBridge.requestSyncCheck(
              project._id,
              projectPath,
              versionFiles
            );
            if (result.success && result.fileStatuses) {
              return result.fileStatuses.map((fs) => ({
                ...fs,
                status: fs.status as FileSyncStatus,
              }));
            }
          }

          throw new Error('Could not check local files; sync was cancelled');
        } catch (error) {
          console.error('Error checking status:', error);
          throw error;
        }
      };

      // Use our custom checkStatus function with the fetched version files
      const fileStatuses = await checkStatusWithVersionFiles(syncProjectDir);

      // Filter for files that need to be synced
      const filesToSync = fileStatuses.filter(
        (f) => f.status !== FileSyncStatus.UP_TO_DATE
      );

      if (filesToSync.length === 0) {
        showSuccess('Local files already match this version');
        return;
      }

      // Count different types of changes
      const newerFiles = fileStatuses.filter(
        (f) => f.status === FileSyncStatus.NEWER
      );
      const behindFiles = fileStatuses.filter(
        (f) => f.status === FileSyncStatus.BEHIND
      );
      const deletedFiles = fileStatuses.filter(
        (f) => f.status === FileSyncStatus.DELETED
      );
      const newFiles = fileStatuses.filter(
        (f) => f.status === FileSyncStatus.NEW
      );

      // Analysis complete — stop showing the loading state on the Sync button
      setIsAnalyzing(false);

      // Always show confirmation dialog when syncing to a historical version
      showSyncDialog({
        newerFiles: newerFiles.length,
        behindFiles: behindFiles.length,
        deletedFiles: deletedFiles.length,
        newFiles: newFiles.length,
        fileStatuses,
        syncProjectDir,
        versionFiles,
        version,
        onConfirmSync: async () => {
          await performSync(fileStatuses, syncProjectDir, versionFiles);
        },
      });
    } catch (error) {
      console.error('Error syncing to version:', error);
      reportTransferError('Prepare version restore', error, {
        projectId: project._id,
        projectName: project.name,
        version: version.version,
      });
      showError('Failed to sync to version. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    // Data
    latestVersionData,
    latestVersionLoading,
    latestVersionError,
    previousVersionData,
    previousVersionLoading,
    previousVersionError,
    versionSyncStatus: { syncing: isAnalyzing, progress: 0 },

    // Actions
    handleDownloadVersion,
    handleSyncToVersion,
    refetch,
  };
};
